// pages/Layout.tsx
import React, { useEffect, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { logout } from "../slice/authSlice";
import type { AppDispatch, RootState } from "../store/store";
import { useLocation } from "react-router-dom";
import { useState } from "react";
import { hasPermission } from "../hooks/usePermission";

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  useEffect(() => {
    if (!showProfileMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);

  const isSuperAdmin = user?.role?.toLowerCase() === "super_admin";
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "Signed in user";
  const initials =
    fullName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]?.toUpperCase())
      .join("") || "U";
  const roleLabel = user?.role
    ? user.role
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char: string) => char.toUpperCase())
    : "User";

  const NAV_TABS = [
    // {
    //   id: "home",
    //   label: "Home",
    //   icon: "🏠",
    //   path: "/super-admin/dashboard",
    //   roles: ["SUPER_ADMIN"],
    // },
    {
      id: "dashboard",
      label: "Dashboard",
      icon: "📊",
      path: "/super-admin/super-admin-dashboard",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "users",
      label: "Users",
      icon: "👥",
      path: "/super-admin/tenant-users?view=users",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "tenants",
      label: "Tenants",
      icon: "🏢",
      path: "/super-admin/tenant-users?view=tenants",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "roles",
      label: "Roles",
      icon: "🛡️",
      path: "/super-admin/tenant-users?view=roles",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "assets",
      label: "Assets",
      icon: "📤",
      path: "/super-admin/asset-manager",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "landing-page-builder",
      label: "Landing Page Builder",
      icon: "\u{1F58C}\uFE0F",
      path: "/super-admin/landing-page-builder",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "audit-logs",
      label: "Audit Logs",
      icon: "\u{1F4CB}",
      path: "/super-admin/audit-logs",
      roles: ["SUPER_ADMIN"],
    },
    {
      id: "oversight",
      label: "System Oversight",
      icon: "🖥️",
      path: "/super-admin/system-oversight",
      roles: ["SUPER_ADMIN"],
    },

    {
      id: "admin-dashboard",
      label: "Dashboard",
      icon: "🏠",
      path: "/tenant/dashboard",
      roles: ["CUSTOMER_ADMIN"],
    },
    {
      id: "campaigns",
      label: "Campaigns & Events",
      icon: "📢",
      path: "/tenant/dashboard?tab=campaigns",
      roles: ["CUSTOMER_ADMIN"],
    },
    {
      id: "tenant-assets",
      label: "Asset Ingestion",
      icon: "📤",
      path: "/tenant/dashboard?tab=assets",
      roles: ["CUSTOMER_ADMIN"],
    },

    // Customer Manager — simplified, single-purpose header tabs
    {
      id: "manager-dashboard",
      label: "Dashboard",
      icon: "🏠",
      path: "/customer-manager/dashboard",
      roles: ["CUSTOMER_MANAGER"],
    },
    {
      id: "manager-campaigns",
      label: "Campaign",
      icon: "📢",
      path: "/tenant/dashboard?tab=campaigns-only",
      roles: ["CUSTOMER_MANAGER"],
    },
    {
      id: "manager-events",
      label: "Events",
      icon: "🎪",
      path: "/tenant/dashboard?tab=events-only",
      roles: ["CUSTOMER_MANAGER"],
    },
    {
      id: "manager-products",
      label: "Products",
      icon: "📦",
      path: "/tenant/dashboard?tab=products-only",
      roles: ["CUSTOMER_MANAGER"],
    },
    {
      id: "manager-assets",
      label: "Assets",
      icon: "📤",
      path: "/tenant/dashboard?tab=assets-list",
      roles: ["CUSTOMER_MANAGER"],
    },
    {
      id: "tenant-users",
      label: "User Management",
      icon: "👥",
      path: "/tenant/dashboard?tab=users",
      roles: ["CUSTOMER_ADMIN"],
    },
    {
      id: "commercial",
      label: "Financial & Billing",
      icon: "💰",
      path: "/tenant/dashboard?tab=commercial",
      roles: ["CUSTOMER_ADMIN"],
    },
    {
      id: "guest",
      label: "Guest Access",
      icon: "🔗",
      path: "/tenant/dashboard?tab=guest",
      roles: ["CUSTOMER_ADMIN"],
    },
  ];

  const userRoleUpper = user?.role?.toUpperCase() || "";
  const visibleTabs = NAV_TABS.filter((tab) =>
    tab.roles.includes(userRoleUpper),
  );

  // ---- active-tab resolution ---------------------------------------------
  // Most tenant tabs share ONE pathname (/tenant/dashboard) and differ only by
  // their query param, so pathname alone cannot identify the active tab.
  //
  // A tab is active when its pathname matches AND every query param it pins
  // equals the current URL's value for that param. A tab that pins no params
  // (the Customer Admin "Dashboard", at a bare /tenant/dashboard) is active
  // only when the URL carries none of the params its siblings use — otherwise
  // it stays highlighted on every sibling page.
  const siblingKeysByPath = new Map<string, Set<string>>();
  visibleTabs.forEach((tab) => {
    const [tabPath, tabQuery] = tab.path.split("?");
    if (!siblingKeysByPath.has(tabPath)) siblingKeysByPath.set(tabPath, new Set());
    new URLSearchParams(tabQuery || "").forEach((_value, key) => {
      siblingKeysByPath.get(tabPath)!.add(key);
    });
  });

  const isTabActive = (fullPath: string) => {
    const [tabPath, tabQuery] = fullPath.split("?");
    if (location.pathname !== tabPath) return false;

    const tabParams = new URLSearchParams(tabQuery || "");
    const urlParams = new URLSearchParams(location.search);

    // Exact param match — never a substring test, so "tab=campaigns" does not
    // also match "tab=campaigns-only".
    let pinned = 0;
    let mismatch = false;
    tabParams.forEach((value, key) => {
      pinned += 1;
      if (urlParams.get(key) !== value) mismatch = true;
    });
    if (mismatch) return false;
    if (pinned > 0) return true;

    let siblingParamPresent = false;
    (siblingKeysByPath.get(tabPath) ?? new Set<string>()).forEach((key) => {
      if (urlParams.has(key)) siblingParamPresent = true;
    });
    return !siblingParamPresent;
  };
  return (
    <div className={`min-h-screen bg-gray-100 ${!isSuperAdmin ? "tenant-themed" : ""}`}>
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto px-8 py-3 flex items-center justify-between">
          {/* LEFT */}
<div className="w-[240px] flex items-center gap-3">
  {/* Logo */}
  {(user as any)?.customerOrg?.logoUrl ? (
    <img 
      src={(user as any).customerOrg.logoUrl}
      alt="Tenant Logo"
      className="h-12 w-12 object-contain rounded border border-gray-200"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  ) : (
    <div
      className="h-12 w-12 rounded flex items-center justify-center flex-shrink-0"
      style={
        !isSuperAdmin
          ? { backgroundColor: "var(--color-primary-light, #DBEAFE)" }
          : { backgroundColor: "#DBEAFE" } // Tailwind blue-100 equivalent
      }
    >
      <span
        className="font-bold text-lg"
        style={{ color: !isSuperAdmin ? "var(--color-primary)" : "#2563EB" }}
      >
        {isSuperAdmin ? 'SA' : (user as any)?.customerOrg?.name?.charAt(0)?.toUpperCase() || 'C'}
      </span>
    </div>
  )}
  
  <div className="min-w-0">
    <h1 className="text-xl font-bold text-gray-900 truncate">
      {isSuperAdmin
        ? "Super Admin Panel"
        : (user as any)?.customerOrg?.name || "Customer Portal"}
    </h1>
    <p className="text-xs text-gray-500">Welcome back, {fullName}</p>
  </div>
</div>

          {/* CENTER */}
          <div className="flex-1 flex justify-end pr-4">
            <div className="flex items-center">
              {visibleTabs.map((tab) => {
                const active = isTabActive(tab.path);

                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(tab.path)}
                    style={
                      active && !isSuperAdmin
                        ? { backgroundColor: "var(--color-primary)" }
                        : undefined
                    }
                    className={`px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-semibold transition-all ${
                      active
                        ? isSuperAdmin
                          ? "bg-blue-600 text-white"
                          : "text-white"
                        : "text-slate-600 hover:bg-white hover:text-blue-600"
                    }`}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                    {tab.id === "tenant-assets" && (
                      <span
                        className="text-[10px] rounded-full px-2 py-0.5 bg-gray-100"
                        style={{ color: "var(--color-primary)" }}
                      >
                        New
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              style={!isSuperAdmin ? { backgroundColor: "var(--color-primary)" } : undefined}
              className={`h-12 w-12 rounded-full text-white flex items-center justify-center font-semibold hover:shadow-lg transition ${
                isSuperAdmin ? "bg-blue-600" : ""
              }`}
            >
              {initials}
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 top-full mt-3 w-64 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden">
                <div className="px-5 py-4 border-b">
                  <div className="font-semibold text-gray-900">{fullName}</div>

                  <div className="text-sm text-gray-500">{roleLabel}</div>
                </div>

                {/* {isSuperAdmin && (
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate("/super-admin/dashboard");
                    }}
                    className="w-full text-left px-5 py-3 hover:bg-gray-50"
                  >
                    🏠 Home
                  </button>
                )} */}

                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate("/tickets");
                  }}
                  className="w-full text-left px-5 py-3 hover:bg-gray-50"
                >
                  🎫 Tickets
                </button>

                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate("/tenant/profile");
                  }}
                  className="w-full text-left px-5 py-3 hover:bg-gray-50"
                >
                  👤 Profile
                </button>

                {/* <button
                                    className="w-full text-left px-5 py-3 hover:bg-gray-50"
                                >
                                    🔐 Change Password
                                </button> */}

                <div className="border-t"></div>

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-5 py-3 text-red-600 hover:bg-red-50"
                >
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
